import { useMemo, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { usePlayer } from '../hooks/usePlayer.js'
import { useVideoSync } from '../hooks/useVideoSync.js'
import { langFlag } from '../lib/languageFlags.js'
import { fetchCaptions } from '../lib/api.js'
import {
  type CaptionStyle,
  CAPTION_STYLES,
  STYLE_CONFIG,
  KaraokeCaptions,
  PlainCaption,
} from './Captions.js'

function formatTime(s: number): string {
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${sec.toString().padStart(2, '0')}`
}

function generateBars(count: number): number[] {
  return Array.from({ length: count }, (_, i) =>
    Math.round(Math.sin(i * 0.31) * 9 + Math.random() * 18 + 8),
  )
}

const SPEAKER_COLORS = ['#FF006E', '#00C48C', '#3A0CA3', '#FFBE0B', '#4CC9F0']

export default function PlayerPanel() {
  const player = usePlayer()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const bars = useMemo(() => generateBars(52), [])
  const [downloading, setDownloading] = useState(false)
  const [showCaptions, setShowCaptions] = useState(true)
  const [captionStyle, setCaptionStyle] = useState<CaptionStyle>('yellow')

  const meta = player.jobMeta

  // Video sync — disabled when full-screen is open (only one YT iframe at a time)
  const { videoSrc, videoRef, hasVideo, isYouTube, ytReady } = useVideoSync({
    player,
    containerId: 'pp-yt-player',
    enabled: player.miniOpen && !player.fullScreenOpen,
  })

  // Captions
  const { data: captionsData } = useQuery({
    queryKey: ['captions', player.jobId],
    queryFn: () => fetchCaptions(player.jobId!),
    enabled: !!player.jobId && player.miniOpen,
    staleTime: Infinity,
  })
  const captions = captionsData?.captions
  const allWords = captions?.flatMap(c => c.words ?? []) ?? []
  const hasKaraoke = allWords.length > 0
  const activeSegment = !hasKaraoke
    ? captions?.find(c => player.currentTime >= c.startSeconds && player.currentTime < c.endSeconds)
    : undefined

  const handleDownload = useCallback(async () => {
    if (!player.audioUrl) return
    setDownloading(true)
    try {
      const res = await fetch(player.audioUrl)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${meta?.videoTitle ?? 'dubbed-audio'}.mp3`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(false)
    }
  }, [player.audioUrl, meta?.videoTitle])

  const handleRedub = useCallback(() => {
    player.closePlayers()
    navigate('/dashboard', {
      state: {
        prefill: {
          url: meta?.videoUrl ?? '',
          sourceLang: meta?.sourceLang ?? 'en',
          targetLang: meta?.targetLang ?? 'ko',
          originalVol: 15,
          dubbedVol: 100,
        },
      },
    })
  }, [player, navigate, meta])

  if (!player.miniOpen) return null

  const progress = player.duration > 0 ? player.currentTime / player.duration : 0
  const headIndex = Math.floor(progress * bars.length)

  const handleWaveformClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const pct = (e.clientX - rect.left) / rect.width
    player.seek(pct * player.duration)
  }

  const variant = player.jobId ? `v${(parseInt(player.jobId.slice(-2), 16) % 6) + 1}` : 'v1'
  const langName = meta ? t(`languages.${meta.targetLang}`, meta.targetLang.toUpperCase()) : ''
  const durStr = meta?.durationSeconds ? formatTime(meta.durationSeconds) : ''

  return (
    <div className={`player-panel-v2${player.miniOpen ? ' open' : ''}`}>
      <div className="pp-head">
        <span className="pp-title">{t('shell.nowPlaying')}</span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button className="pp-close" onClick={player.openFullScreen} title="Open full player" style={{ color: 'var(--sub)' }}>
            <svg viewBox="0 0 24 24"><path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" /></svg>
          </button>
          <button className="pp-close" onClick={player.closePlayers}>
            <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
      </div>

      <div className="pp-body">
        {/* Video / Thumbnail area */}
        <div className="pp-thumb" onClick={player.openFullScreen}>
          {/* YouTube iframe */}
          {isYouTube && (
            <div style={{ aspectRatio: '16/9', position: 'relative' }}>
              <div id="pp-yt-player" style={{ position: 'absolute', inset: 0 }} />
              {!ytReady && <div className="skeleton-shimmer" style={{ position: 'absolute', inset: 0 }} />}
            </div>
          )}

          {/* Native video */}
          {!isYouTube && videoSrc && (
            <video
              ref={videoRef}
              src={videoSrc}
              muted
              playsInline
              preload="metadata"
              style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }}
            />
          )}

          {/* Static thumbnail fallback (no video available) */}
          {!hasVideo && (
            meta?.videoThumbnailUrl?.startsWith('http') ? (
              <img className="pp-thumb-img" src={meta.videoThumbnailUrl} alt="" style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }} />
            ) : (
              <div className={`dub-thumb-bg ${variant} pp-thumb-img`} style={{ aspectRatio: '16/9' }} />
            )
          )}

          {/* Captions overlay */}
          {showCaptions && hasVideo && (
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              {hasKaraoke
                ? <KaraokeCaptions words={allWords} currentTime={player.currentTime} style={captionStyle} wordsPerLine={5} />
                : activeSegment
                  ? <PlainCaption text={activeSegment.text} style={captionStyle} />
                  : null}
            </div>
          )}

          <div className="pp-thumb-overlay" />
          <div className="pp-expand-hint">
            <div className="pp-expand-circle">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#fff" strokeWidth="2"><path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" /></svg>
            </div>
            <span className="pp-expand-label">{t('shell.fullPlayer')}</span>
          </div>
          {meta && (
            <div className="pp-thumb-info">
              <div className="pp-thumb-title">{meta.videoTitle}</div>
              <div className="pp-thumb-meta">{durStr}{durStr && langName ? ' · ' : ''}{langName}</div>
            </div>
          )}
        </div>

        {/* Done badge with language */}
        <div className="pp-done-badge">
          <svg viewBox="0 0 24 24" width="11" height="11" stroke="currentColor" fill="none" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
          {langName ? `${t('shell.dubbed', 'Dubbed')} · ${langFlag(meta!.targetLang)} ${langName}` : t('shell.dubbed', 'Dubbed')}
        </div>

        {/* Mini waveform player */}
        <div className="pp-player">
          <div className="waveform" onClick={handleWaveformClick}>
            {bars.map((h, i) => (
              <div
                key={i}
                className={`waveform-bar${i < headIndex ? ' played' : ''}${i === headIndex ? ' head' : ''}`}
                style={{ height: h }}
              />
            ))}
          </div>
          <div className="pp-controls">
            <button className="pp-play" onClick={player.togglePlay}>
              {player.playing ? (
                <svg viewBox="0 0 24 24" width="14" height="14"><rect x="6" y="4" width="4" height="16" fill="white" /><rect x="14" y="4" width="4" height="16" fill="white" /></svg>
              ) : (
                <svg viewBox="0 0 24 24" width="14" height="14"><polygon points="8 5 19 12 8 19" fill="white" /></svg>
              )}
            </button>
            <span className="pp-time">{formatTime(player.currentTime)} / {formatTime(player.duration)}</span>
            <div className="pp-vol">
              {/* CC toggle */}
              {captions && captions.length > 0 && (
                <button
                  className="pp-cc-btn"
                  onClick={e => { e.stopPropagation(); setShowCaptions(v => !v) }}
                  style={{
                    padding: '1px 5px', borderRadius: 4, fontSize: 9, fontWeight: 700,
                    cursor: 'pointer', letterSpacing: '.03em', marginRight: 4, border: 'none',
                    background: showCaptions ? 'var(--cta)' : 'rgba(255,255,255,.08)',
                    color: showCaptions ? '#fff' : 'var(--sub)',
                  }}
                >CC</button>
              )}
              <svg viewBox="0 0 24 24"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M15.54 8.46a5 5 0 010 7.07" /></svg>
              <input
                type="range"
                className="pp-vol-slider"
                min="0"
                max="100"
                value={Math.round(player.volume * 100)}
                style={{ '--val': `${Math.round(player.volume * 100)}%` } as React.CSSProperties}
                onChange={e => player.setVolume(Number(e.target.value) / 100)}
              />
            </div>
          </div>
        </div>

        {/* Caption style swatches */}
        {showCaptions && captions && captions.length > 0 && (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 14 }}>
            {CAPTION_STYLES.map(s => (
              <button
                key={s}
                onClick={() => setCaptionStyle(s)}
                aria-label={s}
                style={{
                  width: 14, height: 14, borderRadius: '50%', cursor: 'pointer', padding: 0,
                  border: captionStyle === s ? '2px solid var(--cta)' : '1.5px solid rgba(128,128,128,0.4)',
                  background: STYLE_CONFIG[s].swatch,
                  transform: captionStyle === s ? 'scale(1.25)' : 'scale(1)',
                  transition: 'transform .1s',
                }}
              />
            ))}
          </div>
        )}

        {/* Speaker chips */}
        {meta?.speakerProfiles && meta.speakerProfiles.length > 0 && (
          <div className="pp-speakers">
            <div className="pp-label">{t('shell.detectedSpeakers', 'Detected speakers')}</div>
            <div className="speaker-chips">
              {meta.speakerProfiles.map((sp, i) => (
                <div key={i} className="speaker-chip">
                  <div className="sc-dot" style={{ background: SPEAKER_COLORS[i % SPEAKER_COLORS.length] }} />
                  <span className="sc-name">{sp.label}</span>
                  <span className="sc-lang">{sp.gender === 'male' ? t('shell.genderMale') : t('shell.genderFemale')} · {meta.sourceLang.toUpperCase()}→{meta.targetLang.toUpperCase()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="pp-label">{t('shell.actions', 'Actions')}</div>
        <div className="pp-actions">
          <button className="pp-action cta" onClick={player.openFullScreen}>
            <svg viewBox="0 0 24 24"><path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" /></svg>
            {t('shell.openFullPlayer', 'Open full audio player')}
          </button>
          {meta?.videoUrl && (
            <button className="pp-action pp-action-yt" onClick={() => window.open(meta.videoUrl, '_blank')}>
              <svg viewBox="0 0 24 24"><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58 2.78 2.78 0 001.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.96A29 29 0 0023 12a29 29 0 00-.46-5.58z" /><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="currentColor" stroke="none" /></svg>
              {t('shell.watchYouTube', 'Watch on YouTube')} ↗
            </button>
          )}
          <button className="pp-action" onClick={() => void handleDownload()} disabled={downloading || !player.audioUrl}>
            <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            {downloading ? t('shell.downloading', 'Downloading...') : t('shell.downloadAudio', 'Download dubbed audio')}
          </button>
          <button className="pp-action" onClick={handleRedub}>
            <svg viewBox="0 0 24 24"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 100-.49" /></svg>
            {t('shell.redub', 'Re-dub in another language')}
          </button>
        </div>
      </div>
    </div>
  )
}
